import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { apiService } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { getAccessToken } from '@/lib/utils';

export const usePageTitle = () => {
  const pathname = usePathname();
  const [shopName, setShopName] = useState<string>('SaarthiQ');
  const { isAuthenticated, isInitialized } = useAuthStore();

  // Fetch shop profile for dynamic title only when authenticated
  useEffect(() => {
    const fetchShopProfile = async () => {
      try {
        // Check if we have a valid access token before making API call
        const token = getAccessToken();
        if (!token) {
          console.log('usePageTitle: No access token available, skipping shop profile fetch');
          return;
        }

        const profile = await apiService.getShopProfile();
        if (profile?.display_name) {
          setShopName(profile.display_name);
        } else if (profile?.shop_name) {
          setShopName(profile.shop_name);
        }
      } catch (error) {
        console.warn('Failed to fetch shop profile for title:', error);
        // Keep default "SaarthiQ" as fallback
      }
    };

    // Only fetch shop profile if user is authenticated, initialized, and we have a token
    if (isAuthenticated && isInitialized && getAccessToken()) {
      fetchShopProfile();
    }
  }, [isAuthenticated, isInitialized]);

  useEffect(() => {
    const getPageTitle = (path: string): string => {
      const baseTitle = shopName;
      
      switch (path) {
        case '/':
          return `Dashboard`;
        case '/invoicing/':
          return `Create Invoice`;
        case '/pos/':
          return `Point of Sale`;
        case '/customers/':
          return `Customers`;
        case '/products/':
          return `Products`;
        case '/orders/':
          return `Orders`;
        case '/transactions/':
          return `Transactions`;
        case '/reports/':
          return `Reports`;
        case '/stock-management/':
          return `Stock Management`;
        case '/advance-payments/':
          return `Advance Payments`;
        case '/categories/':
          return `Categories`;
        case '/pawn/':
          return `Pawn Services`;
        case '/pawn/loans/':
          return `Active Loans`;
        case '/pawn/loans/new/':
          return `New Loan`;
        case '/pawn/history/':
          return `Loan History`;
        case '/pawn/items/':
          return `Pawn Items`;
        case '/pawn/payments/':
          return `Pawn Payments`;
        case '/pawn/reports/':
          return `Pawn Reports`;
        case '/profile/':
          return `Profile`;
        case '/settings/':
          return `Settings`;
        case '/login/':
          return `Login`;
        case '/signup/':
          return `Sign Up`;
        case '/forgot-password/':
          return `Forgot Password`;
        case '/reset-password/':
          return `Reset Password`;
        default:
          // Handle dynamic routes
          if (path.startsWith('/customers/')) {
            return `Customer Details`;
          }
          if (path.startsWith('/pawn/loans/') && path !== '/pawn/loans/new') {
            return `Loan Details`;
          }
          if (path.startsWith('/receipt')) {
            // Extract receipt ID from URL parameters
            const urlParams = new URLSearchParams(window.location.search);
            const receiptId = urlParams.get('id');
            
            if (receiptId) {
              // Check if the title has already been set by ReceiptWrapper
              const currentTitle = document.title;
              if (currentTitle.includes('Invoice #')) {
                // Title already set by ReceiptWrapper, don't override
                return currentTitle;
              }
              
              return `Receipt #${receiptId}`;
            }
            return `Receipt`;
          }
          if (path.startsWith('/invoice-preview')) {
            return `Invoice Preview`;
          }
          if (path.startsWith('/qr-scanner')) {
            return `QR Scanner`;
          }
          if (path.startsWith('/test-')) {
            return `Test Page`;
          }
          return baseTitle;
      }
    };

    const title = getPageTitle(pathname);
    document.title = title;
  }, [pathname, shopName]);
}; 